import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  UsersIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ShareIcon,
  ServerIcon,
  PlusCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  SignalIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { 
  ShieldCheckIcon as ShieldCheckSolid,
  ExclamationTriangleIcon as ExclamationTriangleSolid 
} from '@heroicons/react/24/solid';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useTablesData } from '../../hooks/useTablesData';
import { useMetadataStatus } from '../../hooks/useMetadataStatus';
import { useValidationsSummary } from '../../hooks/useValidationsData';
import { useIssuesData } from '../../hooks/useIssuesData';
import { queryClient } from '../../api/queryClient';
import { useLineageStatus } from '../../hooks/useLineageStatus';
import EnhancedHealthGauge from '../../components/dashboard/EnhancedHealthGauge';
import IssuesCounterWidget from '../../components/dashboard/IssuesCounterWidget';
import HealthTrendChart from '../../components/dashboard/HealthTrendChart';
import IssuesTrendChart from '../../components/dashboard/IssuesTrendChart';
import ConnectionHealthHeatmap from '../../components/dashboard/ConnectionHealthHeatmap';
import TopFailingValidations from '../../components/dashboard/TopFailingValidations';
import DashboardFilters from '../../components/dashboard/DashboardFilters';
import ROIWidget from '../../components/dashboard/ROIWidget';
import { useHealthTrend, useIssuesSummary, useConnectionHeatmap, useTopFailingValidations } from '../../hooks/useDashboardAnalytics';
import { useInView } from '../../hooks/useInView';
import TierStatusBadge, { TierCompletionSummary, MetadataFreshnessIndicator } from '../../components/metadata/TierStatusBadge';

const DataQualityCommandCenter = () => {
  const { connections, activeConnection, loading: connectionsLoading } = useConnection();
  const { updateBreadcrumbs, showNotification } = useUI();
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [dateRange, setDateRange] = useState(30);

  // Lineage status via React Query — was a raw useEffect that re-fired on
  // every mount (including StrictMode double-mount). The hook dedupes via
  // shared queryKey and caches for 5 minutes.
  const { data: lineageStatus, isLoading: lineageLoading } = useLineageStatus();

  // Get connectionId safely
  const connectionId = activeConnection?.id;

  // Set breadcrumbs
  useEffect(() => {
    updateBreadcrumbs([
      { name: 'Data Quality Command Center', href: '/dashboard' }
    ]);
  }, [updateBreadcrumbs]);

  // Track initial load completion
  useEffect(() => {
    if (!connectionsLoading && !initialLoadComplete) {
      setInitialLoadComplete(true);
    }
  }, [connectionsLoading, initialLoadComplete]);

  // Use React Query with existing hooks
  const tablesQuery = useTablesData(connectionId, {
    enabled: !!connectionId
  });

  const metadataQuery = useMetadataStatus(connectionId, {
    enabled: !!connectionId,
    refetchInterval: (data) => {
      return (data?.pending_tasks?.length > 0) ? 5000 : false;
    },
  });

  const validationsQuery = useValidationsSummary(connectionId, {
    enabled: !!connectionId
  });

  const issuesQuery = useIssuesData({
    enabled: true // Issues are organization-wide, not connection-specific
  });

  // Dashboard analytics queries with filters applied
  const healthTrendQuery = useHealthTrend(dateRange, null, {
    enabled: true, // Organization-wide health trend
    refetchInterval: 60000 // Refresh every minute
  });

  const issuesSummaryQuery = useIssuesSummary('current', {
    enabled: true,
    refetchInterval: 60000
  });

  // Below-the-fold widgets: defer fetching until they're about to scroll
  // into view. Without this, a stack of expensive analytics queries
  // (especially connection-heatmap, which the backend log shows takes
  // ~3.7s) competes for the auth-tax pool during first paint and starves
  // above-the-fold queries. Each widget gets its own ref + visibility flag
  // so the queries fire individually as they become reachable.
  const [heatmapRef, heatmapInView] = useInView();
  const [topFailingRef, topFailingInView] = useInView();
  const [roiRef, roiInView] = useInView();

  const connectionHeatmapQuery = useConnectionHeatmap({
    enabled: heatmapInView,
    refetchInterval: 300000 // Refresh every 5 minutes
  });

  const topFailingValidationsQuery = useTopFailingValidations(10, dateRange, {
    enabled: topFailingInView,
    refetchInterval: 60000 // Refresh every minute
  });

  // Extract data safely with fallbacks. useTablesData's `select` returns
  // `{tables, tierStatus, count}` (an object), not a bare array — the
  // previous `Array.isArray(...).length` check always fell through to 0,
  // which is why the Dashboard's "Data Objects Tracked" card showed 0
  // while Data Explorer (which reads `tablesData.tables`) showed the
  // correct count.
  const tablesCount = tablesQuery.data?.count ?? 0;
  const validationsCount = validationsQuery.data?.total_count || 0;
  const failedValidations = validationsQuery.data?.failing_count || 0;
  const passingValidations = validationsCount - failedValidations;
  const healthScore = validationsQuery.data?.overall_health_score || 0;

  // Extract actionable issues data
  const issuesData = issuesQuery.data || { issues: {}, summary: {} };
  const issuesSummary = issuesData.summary || {};
  const allIssues = issuesData.issues || {};
  
  // Calculate actionable issues metrics (different from validation failures)
  const openIssues = issuesSummary.open_issues || 0;
  const criticalIssues = issuesSummary.critical_issues || 0;
  const totalIssues = issuesSummary.total_issues || 0;
  const highPriorityIssues = Array.isArray(allIssues.high_priority) ? allIssues.high_priority.filter(issue => issue.status === 'open').length : 0;
  const mediumPriorityIssues = Array.isArray(allIssues.medium_priority) ? allIssues.medium_priority.filter(issue => issue.status === 'open').length : 0;
  const lowPriorityIssues = Array.isArray(allIssues.low_priority) ? allIssues.low_priority.filter(issue => issue.status === 'open').length : 0;
  
  // Use actual tables count since the card links to data explorer
  const dataObjectsTracked = tablesCount; // Total tables/views in the connection
  const relationshipsCount = lineageStatus?.total_relationships || 0; // Use real relationships count
  const stakeholdersImpacted = Math.floor(openIssues * 0.8); // Keep estimate for stakeholders as it's reasonable

  // Dashboard analytics data
  const healthTrendData = healthTrendQuery.data || {};
  const issuesSummaryData = issuesSummaryQuery.data || {};
  const connectionHeatmapData = connectionHeatmapQuery.data || {};

  // Use validation-based health score (more accurate than issue-based)
  const currentHealthScore = healthScore; // From validations summary (98.55%)
  const previousHealthScore = healthTrendData.previous_score || healthScore;
  const healthTrendDates = healthTrendData.dates || [];
  const healthTrendScores = healthTrendData.scores || [];

  const currentMonthIssues = issuesSummaryData.current_month || {};
  const previousMonthIssues = issuesSummaryData.previous_month || {};

  const heatmapConnections = connectionHeatmapData.connections || [];
  const topFailingValidationsData = topFailingValidationsQuery.data || {};
  const topFailingValidations = topFailingValidationsData.validations || [];

  // Handle refresh all data
  const handleRefreshData = async () => {
    showNotification('Refreshing data quality metrics...', 'info');
    if (connectionId) {
      queryClient.invalidateQueries(['schema-tables', connectionId]);
      queryClient.invalidateQueries(['metadata-status', connectionId]);
      queryClient.invalidateQueries(['validations-summary', connectionId]);
    }
    queryClient.invalidateQueries(['issues-data']);
    queryClient.invalidateQueries(['healthTrend']);
    queryClient.invalidateQueries(['issuesSummary']);
    queryClient.invalidateQueries(['connectionHeatmap']);
    queryClient.invalidateQueries(['topFailingValidations']);
    showNotification('Data quality metrics refreshed', 'success');
  };

  // Show loading state ONLY during initial load
  if (connectionsLoading && !initialLoadComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto" />
          <p className="mt-4 text-sm text-gray-500">Loading your data quality command center...</p>
        </div>
      </div>
    );
  }

  // Show empty state only if we've finished loading and there are no connections
  if ((!connectionsLoading || initialLoadComplete) && (!connections || connections.length === 0)) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mb-6">
          <ShieldCheckSolid className="h-12 w-12 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Start Monitoring Data Quality</h3>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          Connect your first data source to begin proactive data quality monitoring, 
          lineage tracking, and collaborative issue resolution.
        </p>
        <div className="space-y-4">
          <Link
            to="/connections/new"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <PlusCircleIcon className="-ml-1 mr-2 h-5 w-5" />
            Connect Data Source
          </Link>
        </div>
      </div>
    );
  }

  const isLoading = tablesQuery.isLoading || metadataQuery.isLoading || validationsQuery.isLoading || issuesQuery.isLoading || lineageLoading || healthTrendQuery.isLoading || issuesSummaryQuery.isLoading || connectionHeatmapQuery.isLoading || topFailingValidationsQuery.isLoading;
  const isRefreshing = tablesQuery.isFetching || metadataQuery.isFetching || validationsQuery.isFetching || issuesQuery.isFetching || healthTrendQuery.isFetching || issuesSummaryQuery.isFetching || connectionHeatmapQuery.isFetching || topFailingValidationsQuery.isFetching;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <ShieldCheckSolid className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Data Quality Command Center</h1>
              <p className="text-gray-600">
                Proactive monitoring • Collaborative resolution • Complete visibility
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Real-time refresh indicator */}
          {isRefreshing && !isLoading && (
            <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
              <span>Updating...</span>
            </div>
          )}

          {activeConnection && (
            <button
              onClick={handleRefreshData}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="-ml-1 mr-2 h-4 w-4" />
                  Refresh Data
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Dashboard Filters */}
      <DashboardFilters
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        loading={isLoading}
      />

      {/* Health Score & Critical Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Enhanced Health Gauge — waits only on its own data sources
            (validations summary + health trend for previousScore) instead
            of the aggregate isLoading. Previously this card stayed in the
            loading skeleton until the slowest dashboard query resolved
            (often validationsQuery at ~3s post-render because activeConnection
            settles asynchronously), even though its own queries had returned.

            Also treats `isError` as "still loading" so a transient 503
            (Supabase HTTP/2 disconnect → backend 503 → React Query retries)
            doesn't briefly render 0% before the retry succeeds. */}
        <div className="lg:col-span-1">
          <EnhancedHealthGauge
            score={currentHealthScore}
            previousScore={previousHealthScore}
            loading={
              // Truly loading (initial fetch, no data yet) OR
              // errored AND React Query is still retrying. Once all retries
              // are exhausted, fall through to render whatever data we have
              // — previously isError alone kept this in loading FOREVER
              // (no auto-recovery), so a single transient failure on
              // /validations/summary stuck the gauge in a loading skeleton
              // until the user manually refreshed the page.
              validationsQuery.isLoading ||
              (validationsQuery.isError && validationsQuery.isFetching) ||
              healthTrendQuery.isLoading
            }
          />
        </div>

        {/* Issues Counter Widget — waits only on issuesSummaryQuery, the
            single source of both currentMonth and previousMonth. Same
            reason as above: the aggregate isLoading was making this card
            late even though /api/dashboard/issues-summary returns in ~200ms. */}
        <div className="lg:col-span-2">
          <IssuesCounterWidget
            currentMonth={currentMonthIssues}
            previousMonth={previousMonthIssues}
            loading={issuesSummaryQuery.isLoading}
          />
        </div>
      </div>

      {/* Trend Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HealthTrendChart
          dates={healthTrendDates}
          scores={healthTrendScores}
          currentScore={healthScore}
          loading={healthTrendQuery.isLoading}
          days={dateRange}
        />
        <IssuesTrendChart
          dates={healthTrendDates}
          scores={healthTrendScores}
          loading={healthTrendQuery.isLoading}
          days={dateRange}
        />
      </div>

      {/* Connection Health Heatmap — query deferred until in view (P4) */}
      <div ref={heatmapRef}>
        <ConnectionHealthHeatmap
          connections={heatmapConnections}
          loading={!heatmapInView || connectionHeatmapQuery.isLoading}
        />
      </div>

      {/* Top Failing Validations — query deferred until in view (P4) */}
      <div ref={topFailingRef}>
        <TopFailingValidations
          validations={topFailingValidations}
          loading={!topFailingInView || topFailingValidationsQuery.isLoading}
          days={dateRange}
        />
      </div>

      {/* ROI Metrics Widget — hidden for now. The current calculation
          isn't credible enough to show prospects (placeholder factors,
          not configurable per org). Keep the import and ref wiring so
          re-enabling is a one-line revert when the math is ready. */}
      {false && (
        <div ref={roiRef}>
          <ROIWidget days={dateRange} showBreakdown={true} enabled={roiInView} />
        </div>
      )}

      {/* Metadata Collection Status - NEW TIER-AWARE SECTION */}
      {connectionId && metadataQuery.data?.tierStatus && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                <SignalIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Metadata Collection Status</h3>
                <p className="text-sm text-gray-600">Tiered metadata system - real-time freshness</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MetadataFreshnessIndicator tierStatus={metadataQuery.data.tierStatus} />
              <TierCompletionSummary tierStatus={metadataQuery.data.tierStatus} />
            </div>
          </div>

          {/* Tier Status Badges */}
          <div className="mt-4">
            <TierStatusBadge tierStatus={metadataQuery.data.tierStatus} compact={false} showLabels={false} />
          </div>

          {/* Coverage Info */}
          {metadataQuery.data.tierStatus.coverage && (
            <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {metadataQuery.data.tierStatus.coverage.tables_with_schema || 0}
                </div>
                <div className="text-xs text-gray-600">Tables with Schema</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {metadataQuery.data.tierStatus.coverage.tables_with_profiling || 0}
                </div>
                <div className="text-xs text-gray-600">Tables with Profiling</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {metadataQuery.data.tierStatus.coverage.coverage_percentage || 0}%
                </div>
                <div className="text-xs text-gray-600">Full Coverage</div>
              </div>
            </div>
          )}

          {/* Stale Metadata Warning */}
          {metadataQuery.data.tierStatus.isStale && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium text-yellow-800">Metadata May Be Stale</div>
                <div className="text-xs text-yellow-700 mt-1">
                  Some tables haven't been updated recently. Consider refreshing metadata collection.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Data Ecosystem Overview — each card waits on its own data
          source instead of the aggregate isLoading so cards paint as
          their respective queries resolve. */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Data Objects Being Tracked */}
        {tablesQuery.isLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="h-9 bg-gray-200 rounded w-20 mb-1 animate-pulse" />
            <div className="h-5 bg-gray-200 rounded w-32 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-36 animate-pulse" />
          </div>
        ) : (
          <Link
            to="/explorer"
            className="group bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors duration-200">
                <ServerIcon className="h-6 w-6 text-blue-600" />
              </div>
              <ArrowTrendingUpIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors duration-200" />
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {dataObjectsTracked.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Data Objects Tracked</div>
            <div className="text-xs text-gray-500 mt-2">Tables • Views • Models</div>
          </Link>
        )}

        {/* Lineage Relationships */}
        {lineageLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="h-9 bg-gray-200 rounded w-20 mb-1 animate-pulse" />
            <div className="h-5 bg-gray-200 rounded w-36 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-40 animate-pulse" />
          </div>
        ) : (
          <Link
            to="/lineage"
            className="group bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors duration-200">
                <ShareIcon className="h-6 w-6 text-purple-600" />
              </div>
              <EyeIcon className="h-4 w-4 text-gray-400 group-hover:text-purple-500 transition-colors duration-200" />
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {relationshipsCount.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Lineage Relationships</div>
            <div className="text-xs text-gray-500 mt-2">Dependencies • Flow • Impact</div>
          </Link>
        )}

        {/* Active Validations */}
        {validationsQuery.isLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="h-9 bg-gray-200 rounded w-20 mb-1 animate-pulse" />
            <div className="h-5 bg-gray-200 rounded w-32 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-44 animate-pulse" />
          </div>
        ) : (
          <Link
            to="/validations"
            className="group bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors duration-200">
                <ChartBarIcon className="h-6 w-6 text-green-600" />
              </div>
              <SignalIcon className="h-4 w-4 text-gray-400 group-hover:text-green-500 transition-colors duration-200" />
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {validationsCount.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Active Validations</div>
            <div className="text-xs text-gray-500 mt-2">Quality Rules • Monitors • Checks</div>
          </Link>
        )}

        {/* Total Issues */}
        {issuesQuery.isLoading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse" />
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="h-9 bg-gray-200 rounded w-20 mb-1 animate-pulse" />
            <div className="h-5 bg-gray-200 rounded w-24 mb-2 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-48 animate-pulse" />
          </div>
        ) : (
          <Link
            to="/issues"
            className="group bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-2 rounded-lg group-hover:opacity-90 transition-opacity duration-200 ${
                openIssues === 0 ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {openIssues === 0 ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                ) : (
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                )}
              </div>
              <ArrowTrendingUpIcon className={`h-4 w-4 transition-colors duration-200 ${
                openIssues === 0 ? 'text-gray-400 group-hover:text-green-500' : 'text-gray-400 group-hover:text-red-500'
              }`} />
            </div>
            <div className={`text-3xl font-bold mb-1 ${
              openIssues === 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {openIssues.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Open Issues</div>
            <div className="text-xs text-gray-500 mt-2">Actionable Items • Requiring Resolution</div>
          </Link>
        )}
      </div>


      {/* Recent Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Issues & Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            <Link to="/issues" className="text-sm font-medium text-blue-600 hover:text-blue-700">
              View All
            </Link>
          </div>

          {issuesQuery.isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="w-5 h-5 bg-gray-200 rounded mt-0.5 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded w-5/6 animate-pulse" />
                  </div>
                  <div className="w-10 h-3 bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {openIssues > 0 ? (
                <>
                  {(criticalIssues > 0 || highPriorityIssues > 0) && (
                    <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg border border-red-200">
                      <ExclamationTriangleSolid className="h-5 w-5 text-red-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-red-800">
                          {criticalIssues + highPriorityIssues} Critical & High Priority Issues
                        </div>
                        <div className="text-xs text-red-600 mt-1">
                          Require immediate attention • {stakeholdersImpacted} stakeholders impacted
                        </div>
                      </div>
                      <div className="text-xs text-red-500">Active</div>
                    </div>
                  )}

                  {failedValidations > 0 && (
                    <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <ClockIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-yellow-800">
                          {failedValidations} Data Quality Validations Failed
                        </div>
                        <div className="text-xs text-yellow-600 mt-1">
                          {passingValidations} validations passed • May need issue creation
                        </div>
                      </div>
                      <div className="text-xs text-yellow-500">Recent</div>
                    </div>
                  )}

                  {(mediumPriorityIssues > 0 || lowPriorityIssues > 0) && (
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <ClockIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-blue-800">
                          {mediumPriorityIssues + lowPriorityIssues} Medium & Low Priority Issues
                        </div>
                        <div className="text-xs text-blue-600 mt-1">
                          In progress • Monitoring needed
                        </div>
                      </div>
                      <div className="text-xs text-blue-500">Ongoing</div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-6">
                  <CheckCircleIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <div className="text-sm font-medium text-gray-900">No open issues</div>
                  <div className="text-xs text-gray-500">All actionable items have been resolved</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              to="/validations"
              className="flex items-center p-4 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors duration-200 group"
            >
              <ChartBarIcon className="h-6 w-6 text-blue-600 mr-3 group-hover:scale-110 transition-transform duration-200" />
              <div>
                <div className="text-sm font-medium text-blue-900">Run Validations</div>
                <div className="text-xs text-blue-600">Check data quality</div>
              </div>
            </Link>
            
            <Link
              to="/lineage"
              className="flex items-center p-4 bg-purple-50 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors duration-200 group"
            >
              <ShareIcon className="h-6 w-6 text-purple-600 mr-3 group-hover:scale-110 transition-transform duration-200" />
              <div>
                <div className="text-sm font-medium text-purple-900">View Lineage</div>
                <div className="text-xs text-purple-600">Trace data flow</div>
              </div>
            </Link>
            
            <Link
              to="/explorer"
              className="flex items-center p-4 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors duration-200 group"
            >
              <ServerIcon className="h-6 w-6 text-green-600 mr-3 group-hover:scale-110 transition-transform duration-200" />
              <div>
                <div className="text-sm font-medium text-green-900">Explore Data</div>
                <div className="text-xs text-green-600">Browse objects</div>
              </div>
            </Link>
            
            <Link
              to="/connections/new"
              className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors duration-200 group"
            >
              <PlusCircleIcon className="h-6 w-6 text-gray-600 mr-3 group-hover:scale-110 transition-transform duration-200" />
              <div>
                <div className="text-sm font-medium text-gray-900">Add Source</div>
                <div className="text-xs text-gray-600">Connect data</div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataQualityCommandCenter;